import { OnSubmitHandler, useInputGroup } from '@wikus-ui/hooks/useInputGroup';
import { WikusButton, WikusButtonRow } from '@wikus-ui/components/core/button';
import { WikusInputRow } from '@wikus-ui/components/core/input';
import { useIntl } from 'react-intl';

import * as CustomerUserActions from './../../../actions/customer-user';
import * as InfoActions from './../../../actions/info';

import {
  CustomerUser,
  CustomerUserActions as CustomerUserActionsModel
} from './../../../model/customer-user';
import { RootState } from './../../../reducers';
import { useActions } from './../../../actions';

import { CustomerRouteParams } from '../../../pages/customer/models';
import { WikusSelect } from '@wikus-ui/components/core/select';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

type TEditCustomerUserFormProps = {
  onEdit?: () => void;
  onAbort?: () => void;
  onDelete?: () => void;
  currentUserId?: number;
  customerUser: CustomerUser | null;
  showForm: boolean;
};

export const EditCustomerUserForm = ({
  onEdit,
  onAbort,
  onDelete,
  currentUserId,
  customerUser,
  showForm,
}: TEditCustomerUserFormProps) => {
  const { id: customerId } = useParams<CustomerRouteParams>();
  const intl = useIntl();
  const EditCustomerUserInputGroup = 'edit-user';

  const customerUserActions: typeof CustomerUserActions =
    useActions(CustomerUserActions);
  const infoActions: typeof InfoActions = useActions(InfoActions);

  const info = useSelector((state: RootState) => state.info);

  const _onSubmit: OnSubmitHandler = async () => {
    if (!isValid) {
      return;
    }

    const { type } = await customerUserActions.updateCustomerUser(
      value,
      customerId,
      // we can only see the form if there is a user
      customerUser!.id
    );

    const isSuccess =
      type === CustomerUserActionsModel.UPDATE_CUSTOMER_USER_SUCCESS;

    if (isSuccess) {
      onEdit && onEdit();
    }

    return isSuccess;
  };

  const {
    value,
    setGroup,
    isValid,
    onSubmit,
    createInputProps,
    createSwitchProps,
    isSubmitting,
  } = useInputGroup(EditCustomerUserInputGroup, _onSubmit);

  useEffect(() => {
    setGroup({
      value: {
        country: customerUser?.country,
        language: customerUser?.language,
        orderMail: customerUser?.orderMail,
        sendToCustomer: customerUser?.sendToCustomer,
        featureAutoCreateArticles: customerUser?.featureAutoCreateArticles,
        featureAutoConfirmArticles: customerUser?.featureAutoConfirmArticles,
        featureMachines: customerUser?.featureMachines,
        featureCustomArticles: customerUser?.featureCustomArticles,
        fulfillThreshold: customerUser?.fulfillThreshold,
      },
    });
  }, [customerUser]);

  useEffect(() => {
    infoActions.fetchInfo();
  }, []);

  if (!showForm || !info) return null;

  return (
    <form onSubmit={onSubmit}>
      <WikusInputRow>
        <WikusSelect
          xs={6}
          {...createInputProps('country')}
          options={Object.keys(info.countries).map((key) => ({
            id: key,
            name: info?.countries[key],
          }))}
        />
        <WikusSelect
          xs={6}
          {...createInputProps('language')}
          options={Object.keys(info.languages).map((key) => ({
            id: key,
            name: info?.languages[key],
          }))}
        />
      </WikusInputRow>
      <WikusButtonRow style={{ justifyContent: 'space-between' }}>
        {onDelete && (
          <WikusButton
            onClick={onDelete}
            color="secondary"
            variant="outlined"
            disabled={isSubmitting || currentUserId === customerUser?.id}
          >
            {intl.formatMessage({
              id: 'delete',
            })}
          </WikusButton>
        )}
        <div style={{ display: 'flex', gap: '16px' }}>
          <WikusButton type="submit" color="primary" disabled={isSubmitting || isValid === undefined}>
            {intl.formatMessage({
              id: 'create-user.submit',
            })}
          </WikusButton>
          <WikusButton
            onClick={onAbort}
            color="primary"
            variant="outlined"
            disabled={isSubmitting}
          >
            {intl.formatMessage({
              id: 'create-user.abort',
            })}
          </WikusButton>
        </div>
      </WikusButtonRow>
    </form>
  );
};
